<script setup>
import {defineProps, ref, onMounted, defineEmits, computed, nextTick} from 'vue'
import TheModal from '@shared-components/ui/modals/TheModal.vue'
import BaseForm from '@shared-components/base/BaseForm.vue'
import TopUpRequestForm from '@/views/ad-account-requests/top-up/components/manage-funds-modal/TopUpRequestForm.vue'
import WithdrawRequestForm from '@/views/ad-account-requests/top-up/components/manage-funds-modal/WithdrawRequestForm.vue'
import TransferRequestForm from '@/views/ad-account-requests/top-up/components/manage-funds-modal/TransferRequestForm.vue'
import InvoiceModal from '@/views/finance/components/InvoiceModal.vue'
import TheSelect from '@shared-components/ui/selects/TheSelect.vue'
import {storeToRefs} from 'pinia'
import {useCompanyStore} from '@/views/companies/CompanyStore'
import {useAuthenticationStore} from '@/stores'
import {notifyErrors} from '@/services/notify/helpers'
import {useNotification} from '@kyvg/vue3-notification'

const {notify} = useNotification()
const {companiesOptions} = storeToRefs(useCompanyStore())
const {userCompanyHash, isCoreTeam} = storeToRefs(useAuthenticationStore())

// Props & emits
const props = defineProps({
  id: {
    type: String,
    default: 'manage-funds-modal',
  },
  adAccount: {
    type: Object,
    required: false,
    default: null,
  },
})
const emit = defineEmits(['created', 'hidden'])

// Refs
const companyHash = ref('')
const loading = ref(false)
const theModal = ref()
const showInvoiceModal = ref(false)
const defaultFormData = {name: null}
const formData = ref(cloneSafe(defaultFormData))
const formType = ref('Top-Up')
const formTypeOptions = ['Top-Up', 'Withdraw', 'Transfer']
const topUpFormRef = ref()
const withdrawFormRef = ref()
const clientTransferFormRef = ref()
const mapper = {
  'Top-Up': topUpFormRef,
  Withdraw: withdrawFormRef,
  Transfer: clientTransferFormRef,
}

// Computed
const canSubmit = computed(() => !!mapper[formType.value].value?.canSubmit)

// Methods
onMounted(() => {
  init()

  openModal()
})

const init = () => {
  formData.value = cloneSafe(defaultFormData)

  companyHash.value = props.adAccount?.company?.hash || userCompanyHash.value
}
const openModal = () => {
  theModal.value.handleOpenModal()
}
const closeModal = () => {
  theModal.value.handleCloseModal()
}
const openInvoiceModal = () => {
  showInvoiceModal.value = true
}
const selectFormType = option => {
  formType.value = option
}

const handleSubmit = async () => {
  loading.value = true
  try {
    await mapper[formType.value].value?.handleCreate()

    emit('created')
    closeModal()
  } catch (e) {
    notifyErrors(notify, e.data)
  } finally {
    loading.value = false
  }
}

const onCompanyChange = () => {
  if (!companyHash.value) {
    return
  }

  nextTick(() => {
    mapper[formType.value].value?.resetFormAndReloadAccounts()
  })
}

const emitHidden = () => {
  emit('hidden')
}
</script>

<template>
  <Teleport to="body">
    <invoice-modal
      v-if="showInvoiceModal"
      :parent-modal-id="id"
      @hidden="showInvoiceModal = false"
      id="manage-funds-invoice-modal"
    />
  </Teleport>
  <TheModal ref="theModal" @hidden="emitHidden" :id="props.id" title="Manage Funds" class="manage-funds-modal" large>
    <base-form @on-submitted="handleSubmit">
      <div class="modal-body">
        <div class="btn-group btn-row-group w-100 mb-4" role="group">
          <button
            v-for="option in formTypeOptions"
            @click="selectFormType(option)"
            :class="['btn btn-secondary text-capitalize', formType === option ? 'active' : '']"
            type="button"
            :key="`options-top-up-modal-${option}`"
          >
            {{ option }}
          </button>
        </div>
        <div v-if="isCoreTeam" class="form-group row">
          <div class="col">
            <label class="custom fs-5 mb-4 fw-semibold" for="manage-funds-request-company">Company</label>
            <the-select
              v-model="companyHash"
              :options="companiesOptions"
              :disabled="loading"
              :allow_clear="false"
              :can_deselect="false"
              placeholder="Enter Company Name"
              id="manage-funds-request-company"
              @change="onCompanyChange"
              required
            />
          </div>
        </div>
        <template v-if="companyHash">
          <top-up-request-form
            v-if="formType === 'Top-Up'"
            v-model:company="companyHash"
            :ad-account="adAccount"
            :is-user-company-hash="!!userCompanyHash"
            @open-invoice-modal="openInvoiceModal"
            ref="topUpFormRef"
          />
          <withdraw-request-form
            v-else-if="formType === 'Withdraw'"
            v-model:company="companyHash"
            :ad-account="adAccount"
            :is-account-select-by-search="!!userCompanyHash"
            @open-invoice-modal="openInvoiceModal"
            ref="withdrawFormRef"
          />
          <transfer-request-form
            v-model:company="companyHash"
            v-else-if="formType === 'Transfer'"
            :is-account-select-by-search="!!userCompanyHash"
            :ad-account="adAccount"
            @open-invoice-modal="openInvoiceModal"
            ref="clientTransferFormRef"
          />
        </template>
      </div>

      <div class="modal-footer justify-content-end custom gap-4">
        <button type="button" class="btn btn-secondary" @click="closeModal">Close</button>
        <button class="btn btn-primary" :disabled="!canSubmit">Submit</button>
      </div>
    </base-form>
  </TheModal>
</template>

<style lang="scss">
.manage-funds-modal {
  & .modal-header {
    padding: 16px 20px;
  }

  & .modal-title {
    font-size: 18px;
    font-weight: 600;
  }

  & .modal-content {
    border-radius: 16px;
  }

  & .modal-body {
    padding: 20px 26px;
  }

  & .modal-footer {
    padding: 12px 20px;
  }

  & .btn-close {
    position: absolute;
    margin: 0;
    right: 8px;
    top: 8px;
    z-index: 2;
  }
}
</style>
